import React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  styled,
} from '@mui/material';

// Styled Autocomplete component
const StyledAutocomplete = styled(MuiAutocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.2s ease-in-out',
    height: '48px',

    '& fieldset': {
      borderColor:
        theme.palette.mode === 'light'
          ? 'rgba(0, 0, 0, 0.23)'
          : 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor:
        theme.palette.mode === 'light'
          ? 'rgba(0, 0, 0, 0.42)'
          : 'rgba(255, 255, 255, 0.42)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1px',
    },
  },
  '& .MuiAutocomplete-input': {
    padding: '7.5px 4px 7.5px 8px !important',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: '8px',
  },

  // Dropdown styling
  '& .MuiAutocomplete-popper': {
    '& .MuiPaper-root': {
      borderRadius: '12px',
      marginTop: '8px',
      boxShadow: theme.shadows[3],
    },
    '& .MuiAutocomplete-option': {
      padding: '8px 16px',
      borderRadius: '8px',
      margin: '2px 4px',
      '&[aria-selected="true"]': {
        backgroundColor:
          theme.palette.mode === 'light'
            ? 'rgba(25, 118, 210, 0.08)'
            : 'rgba(25, 118, 210, 0.16)',
      },
      '&.Mui-focused': {
        backgroundColor:
          theme.palette.mode === 'light'
            ? 'rgba(0, 0, 0, 0.04)'
            : 'rgba(255, 255, 255, 0.08)',
      },
    },
  },

  // Chips styling
  '& .MuiChip-root': {
    height: '24px',
    margin: '3px',
    borderRadius: '8px',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
    '& .MuiChip-deleteIcon': {
      fontSize: '16px',
      margin: '0 2px 0 -6px',
    },
  },
}));

// Autocomplete component
const Autocomplete = ({
  options = [],
  getOptionLabel,
  label,
  value,
  onChange,
  multiple = false,
  freeSolo = false,
  error = false,
  helperText,
  fullWidth = false,
  disabled = false,
  loading = false,
  ...props
}) => {
  // Handle onChange based on multiple mode
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(event, newValue);
    }
  };

  return (
    <StyledAutocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={handleChange}
      multiple={multiple}
      freeSolo={freeSolo}
      disabled={disabled}
      fullWidth={fullWidth}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
        />
      )}
      {...props}
    />
  );
};

Autocomplete.propTypes = {
  /**
   * Array of options.
   */
  options: PropTypes.array.isRequired,
  /**
   * Function to get the label from an option.
   */
  getOptionLabel: PropTypes.func,
  /**
   * The label of the autocomplete.
   */
  label: PropTypes.string,
  /**
   * The value of the autocomplete.
   */
  value: PropTypes.any,
  /**
   * Callback fired when the value changes.
   */
  onChange: PropTypes.func,
  /**
   * If true, the autocomplete will support multiple selections.
   */
  multiple: PropTypes.bool,
  /**
   * If true, the autocomplete will allow free text entry.
   */
  freeSolo: PropTypes.bool,
  /**
   * If true, the autocomplete will display an error state.
   */
  error: PropTypes.bool,
  /**
   * The helper text to display.
   */
  helperText: PropTypes.node,
  /**
   * If true, the autocomplete will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * If true, the autocomplete will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If true, the autocomplete will display a loading indicator.
   */
  loading: PropTypes.bool,
};

export default Autocomplete;
